import { useEffect, useState, useCallback, useRef } from "react";
import { Container, Grid, Typography, IconButton, Tooltip } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { MainContainer, CardContainer } from "./styles";
import { PageContainer } from "components/PageContainer";
import EditDefaultCostForm from "./Components/EditDefaultCostForm";
import { IDefaultRouteCostsData, IDefaultRouteCostsResponse, IJabuticabaGroup, IVehicleTypeResponse } from "./types";
import { costManagementService } from "services/costManagementService";
import { jabuticabaGroupService } from "services/jabuticabaGroupService";
import NewDefaultCostForm from "./Components/NewDefaultCostForm";
import PageLoader from "components/Table/PageLoader";
import { CustomAccordion } from "components/accordion";
import { ConfirmPopup } from "components/ConfirmPopup";
import NewButton from "components/NewButton";

const getDefaultCostTemplate = (): IDefaultRouteCostsData => ({
  groupId: 0,
  costs: [
    {
      vehicleTypeId: 1,
      initialCapacity: 0,
      finalCapacity: 45,
      cost: 100,
      leaderCommission: 10,
      additionalDelivery: 0,
      unloadingCost: null,
    },
    {
      vehicleTypeId: 2,
      initialCapacity: 46,
      finalCapacity: 60,
      cost: 200,
      leaderCommission: 10,
      additionalDelivery: 0,
      unloadingCost: null,
    },
    {
      vehicleTypeId: 3,
      initialCapacity: 61,
      finalCapacity: 90,
      cost: 300,
      leaderCommission: 10,
      additionalDelivery: 0,
      unloadingCost: null,
    },
    {
      vehicleTypeId: 4,
      initialCapacity: 91,
      finalCapacity: 120,
      cost: 400,
      leaderCommission: 10,
      additionalDelivery: 0,
      unloadingCost: null,
    }
  ]
});

const DefaultCosts = () => {
  const [form, setForm] = useState<IDefaultRouteCostsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<IJabuticabaGroup[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<IVehicleTypeResponse[]>([]);
  const [data, setData] = useState<IDefaultRouteCostsResponse[]>([]);
  const [deletePopup, setDeletePopup] = useState<{
    isOpen: boolean;
    groupId?: number;
    groupName?: string;
  }>({ isOpen: false });
  const formRef = useRef<HTMLDivElement>(null);

  const groupName = (groupId: number) => groups.find(group => group.id === groupId)?.name ?? `Grupo ${groupId}`;

  // Iniciar um novo formulário
  const handleStartNewForm = () => {
    const newForm = getDefaultCostTemplate();
    setForm(newForm);
  };

  // Salvar o formulário
  const handleSaveForm = async (form: IDefaultRouteCostsData) => {
    try {
      const response = await costManagementService.createDefaultCost(form);
      setData(prevData => [...prevData, response]);
      setForm(null);
      toast.success("Valores criados com sucesso");
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao salvar os valores";
      toast.error(errorMessage);
      throw error;
    }
  };

  // Salvar um formulário existente
  const handleSaveExistingForm = async (
    data: IDefaultRouteCostsResponse,
    formToSave: IDefaultRouteCostsData
  ) => {
    try {
      const response = await costManagementService.updateDefaultCost(formToSave);
      setData(prevData => prevData.map(item =>
        item.groupId === response.groupId ? response : item
      ));
      toast.success(`Valores atualizados com sucesso para ${groupName(data.groupId)}`);
      return response;
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao atualizar os valores";
      toast.error(errorMessage);
      throw error;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groups, vehicleTypes, data] = await Promise.all([
        jabuticabaGroupService.getGroups(),
        costManagementService.getVehicleTypes(),
        costManagementService.getDefaultCosts({})
      ]);

    setGroups(groups);
    setVehicleTypes(vehicleTypes);
    setData(data)
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao buscar os dados";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (groupId: number) => {
    try {
      await costManagementService.deleteDefaultCost(groupId);
      setData(prevData => prevData.filter(item => item.groupId !== groupId));
      toast.success(`Valores do grupo ${groupName(groupId)} deletados com sucesso`);
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao deletar os valores do grupo.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, groupId: number) => {
    e.stopPropagation();
    setDeletePopup({ isOpen: true, groupId, groupName: groupName(groupId) });
  };

  const availableGroups = useCallback(() => {
    return groups
      .filter((group: IJabuticabaGroup) => group.active && !data.some(item => item.groupId === group.id));
  }, [groups, data]);

  useEffect(() => {
    fetchData();
  }, []);

  // Scroll para o formulário quando ele for criado
  useEffect(() => {
    if (form && formRef.current) {
      // Aguarda o React renderizar completamente
      setTimeout(() => {
        if (formRef.current) {
          formRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 150);
    }
  }, [form]);

  return (
    <MainContainer>
      <div className="pageContent">
        {loading && <PageLoader height="90vh" />}

        {!loading && (
          <PageContainer>
            <Container
              maxWidth={false}
              sx={{
                mt: 4,
                mb: 4,
                px: 4,
                width: '100%',
                maxWidth: '100%',
                padding: 0
              }}
            >
              <Grid container spacing={2} alignItems="center" marginBottom={4}>
                <Grid item xs>
                  <h3>Valores Base</h3>
                </Grid>
                <Grid item>
                  <NewButton
                    variantType="filled"
                    startIcon={<AddIcon />}
                    onClick={handleStartNewForm}
                    disabled={loading || !!form || availableGroups().length === 0}
                    sx={{ display: data.length === 0 ? 'none' : 'flex' }}
                  >
                    Adicionar
                  </NewButton>
                </Grid>
              </Grid>

              {!form && data.length === 0 && !loading ? (
                <CardContainer>
                  <Typography variant="body1" color="textSecondary" gutterBottom>
                    Nenhum valor padrão configurado
                  </Typography>
                  <NewButton
                    variantType="filled"
                    startIcon={<AddIcon />}
                    onClick={handleStartNewForm}
                    disabled={loading || !!form}
                  >
                    Adicionar
                  </NewButton>
                </CardContainer>
              ) : (
                <>
                  <CustomAccordion
                    data={data}
                    renderSummary={(item) => (
                      <Grid container alignItems="center" justifyContent="space-between">
                        <Grid item>
                          <Typography
                            fontSize={["1rem", "1.1rem"]}
                            fontWeight={600}
                            color={"rgba(0, 0, 0, 0.87)"}
                          >
                              {groupName(item.groupId)}
                          </Typography>
                        </Grid>
                        <Grid item mr={2}>
                          <Tooltip title="Deletar valores do grupo">
                            <IconButton
                              color="default"
                              onMouseEnter={e => e.currentTarget.style.color = '#c0392b'}
                              onMouseLeave={e => e.currentTarget.style.color = ''}
                              onClick={(e) => handleDeleteClick(e, item.groupId)}
                              disabled={loading}
                          >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                  >
                    {(item) => (
                      <EditDefaultCostForm
                        form={item}
                        vehicleTypes={vehicleTypes}
                        onSave={(updatedForm) => handleSaveExistingForm(item, updatedForm)}
                        loading={loading}
                      />
                    )}
                  </CustomAccordion>

                  {/* Formulário de criação */}
                  {form && (
                    <CardContainer ref={formRef}>
                      <NewDefaultCostForm
                        form={form}
                        vehicleTypes={vehicleTypes}
                        groups={availableGroups()}
                        onSave={handleSaveForm}
                        onRemove={() => setForm(null)}
                        loading={loading}
                      />
                    </CardContainer>
                  )}
                </>
              )}
            </Container>
          </PageContainer>
        )}

        <ConfirmPopup
          states={[deletePopup.isOpen, (value: boolean) => setDeletePopup(prev => ({ ...prev, isOpen: value }))]}
          handleDelete={() => {
            if (deletePopup.groupId) {
              handleDelete(deletePopup.groupId);
              setDeletePopup({ isOpen: false });
            }
          }}
          message={`Tem certeza que deseja excluir os valores do grupo "${deletePopup.groupName}"?`}
        />
      </div>
    </MainContainer>
  );
};

export default DefaultCosts;
